import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ArrowPathIcon,
  BookOpenIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";
import { AIGenerator } from "../../utils/AIGenerator";
import { aiGeneratorConfigs } from "../../../config/aiGeneratorConfigs";
import { buildHseDocumentAiGuide } from "./hseDocumentAiGuide";
import {
  formatExistingDocumentsOutline,
  formatStructureOutline,
  parseImportedHseDocumentsJson,
  SAMPLE_HSE_DOCUMENTS_JSON,
  serializeDisciplineStructureForExport,
  serializeExistingDocumentsForAi,
  serializeHseDocumentsPayloadForExport,
} from "./hseDocumentImportUtils";
import { bulkImportHseDocuments, getHseDocuments } from "../../../api/hse/documents";
import { invalidateDisciplinesCache } from "../../../api/hse/disciplines";

/**
 * Bulk JSON / AI dialog for HSE/KS documents (create docs + optional new structure).
 */
export function HseDocumentJsonDialog({
  open,
  onClose,
  disciplines = [],
  onImported,
  defaultDocumentType = "instruction",
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("json");
  const [draft, setDraft] = useState("");
  const [parseError, setParseError] = useState("");
  const [importing, setImporting] = useState(false);
  const [createMissing, setCreateMissing] = useState(false);
  const [atomic, setAtomic] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const prevOpen = useRef(false);

  const companyDisciplines = useMemo(() => {
    if (Array.isArray(disciplines)) return disciplines;
    return disciplines?.company_disciplines || [];
  }, [disciplines]);

  const aiGuide = useMemo(
    () => buildHseDocumentAiGuide(companyDisciplines, existingDocuments),
    [companyDisciplines, existingDocuments]
  );

  const structureOutline = useMemo(
    () => formatStructureOutline(companyDisciplines),
    [companyDisciplines]
  );

  const documentsOutline = useMemo(
    () => formatExistingDocumentsOutline(existingDocuments),
    [existingDocuments]
  );

  const existingStructureJson = useMemo(
    () =>
      JSON.stringify(
        serializeDisciplineStructureForExport(companyDisciplines),
        null,
        2
      ),
    [companyDisciplines]
  );

  const existingDocumentsJson = useMemo(
    () =>
      JSON.stringify(serializeExistingDocumentsForAi(existingDocuments), null, 2),
    [existingDocuments]
  );

  useEffect(() => {
    if (!open) {
      prevOpen.current = false;
      return undefined;
    }
    if (prevOpen.current) return undefined;
    prevOpen.current = true;

    setDraft(
      serializeHseDocumentsPayloadForExport({
        create_missing_structure: false,
        atomic: false,
        access_type: "all",
        structure: [],
        documents: [
          {
            title: "",
            description: "",
            document_type: defaultDocumentType,
            discipline_code: companyDisciplines[0]?.code || "",
            category_code: companyDisciplines[0]?.categories?.[0]?.code || "hms",
            chapter_number:
              companyDisciplines[0]?.categories?.[0]?.chapters?.[0]?.number ||
              "",
            content_type: "text",
            content: "",
          },
        ],
      })
    );
    setParseError("");
    setTab("json");
    setCreateMissing(false);
    setAtomic(false);
    setImportResult(null);

    let cancelled = false;
    setLoadingDocuments(true);
    getHseDocuments({})
      .then((response) => {
        if (cancelled) return;
        const rows = response?.documents || response?.data || response || [];
        setExistingDocuments(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setExistingDocuments([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingDocuments(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, companyDisciplines, defaultDocumentType]);

  const handleCopyJson = async () => {
    try {
      await copyTextToClipboard(draft);
      toast.success(t("hse.documents.json.copy_success"));
    } catch {
      toast.error(t("hse.documents.json.clipboard_failed"));
    }
  };

  const handleCopyAiGuide = async () => {
    try {
      await copyTextToClipboard(aiGuide);
      toast.success(t("hse.documents.json.ai_guide_copied"));
    } catch {
      toast.error(t("hse.documents.json.clipboard_failed"));
    }
  };

  const handleReloadStructureSample = () => {
    setDraft(
      serializeHseDocumentsPayloadForExport({
        create_missing_structure: createMissing,
        atomic,
        access_type: "all",
        structure: [],
        documents: [],
      })
    );
    setParseError("");
    toast.success(t("hse.documents.json.reload_success"));
  };

  const handleInsertSample = () => {
    setDraft(SAMPLE_HSE_DOCUMENTS_JSON);
    setCreateMissing(true);
    setParseError("");
  };

  const runImport = async (payload) => {
    setImporting(true);
    setImportResult(null);
    try {
      const result = await bulkImportHseDocuments(payload);
      invalidateDisciplinesCache();
      const counts = result?.counts || {};
      toast.success(
        result?.message ||
          t("hse.documents.json.imported", {
            documents: counts.documents || 0,
            chapters: counts.chapters || 0,
            categories: counts.categories || 0,
            disciplines: counts.disciplines || 0,
          })
      );
      if (Array.isArray(result?.errors) && result.errors.length) {
        toast.error(result.errors.slice(0, 3).join(" · "));
      }
      if (Array.isArray(result?.warnings) && result.warnings.length) {
        toast(result.warnings.slice(0, 2).join(" · "), { icon: "⚠️" });
      }
      setImportResult(result);
      onImported?.(result);
      // Refresh inventory so the next AI pass sees newly created docs
      try {
        const response = await getHseDocuments({});
        const rows = response?.documents || response?.data || response || [];
        setExistingDocuments(Array.isArray(rows) ? rows : []);
      } catch {
        // keep previous inventory
      }
    } catch (e) {
      const apiErrors = e?.response?.data?.errors;
      const message =
        (Array.isArray(apiErrors) && apiErrors.join(", ")) ||
        e?.response?.data?.message ||
        e?.message ||
        t("hse.documents.json.import_failed");
      setParseError(message);
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  const handleApply = async () => {
    try {
      const parsed = parseImportedHseDocumentsJson(draft);
      if (parsed.droppedCount > 0) {
        toast.error(
          t("hse.documents.json.dropped_rows", {
            count: parsed.droppedCount,
            detail: (parsed.dropReasons || []).slice(0, 5).join(", "),
          })
        );
      }
      // UI toggles override JSON flags so operators stay in control
      await runImport({
        ...parsed,
        create_missing_structure: createMissing || parsed.create_missing_structure,
        atomic: atomic || parsed.atomic,
        access_type: parsed.access_type || "all",
      });
    } catch (e) {
      const code = e?.code || "unknown";
      const key = `hse.documents.json.error_${code}`;
      const msg = t(key);
      const display = msg !== key ? msg : e?.message || String(e);
      setParseError(display);
      toast.error(display);
    }
  };

  const createdDocs = importResult?.created?.documents || [];

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      size="4xl"
      title={t("hse.documents.json.dialog_title")}
      zIndex={4000}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
          <button
            type="button"
            onClick={() => setTab("json")}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
              tab === "json" ? "bg-sky-100 text-sky-900" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <DocumentTextIcon className="h-5 w-5 shrink-0" />
            {t("hse.documents.json.tab_json")}
          </button>
          <button
            type="button"
            onClick={() => setTab("ai")}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
              tab === "ai" ? "bg-slate-100 text-slate-900" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            <BookOpenIcon className="h-5 w-5 shrink-0" />
            {t("hse.documents.json.tab_ai_guide")}
          </button>
        </div>

        {tab === "json" ? (
          <>
            <p className="text-sm text-gray-600">{t("hse.documents.json.hint")}</p>
            <details className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700">
              <summary className="cursor-pointer font-medium">
                {t("hse.documents.json.current_structure")}
              </summary>
              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap font-mono">
                {structureOutline}
              </pre>
            </details>
            <details className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700" open>
              <summary className="cursor-pointer font-medium">
                {t("hse.documents.json.existing_documents")}
                {loadingDocuments
                  ? ` (${t("hse.documents.json.loading_documents")})`
                  : ` (${existingDocuments.length})`}
              </summary>
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-mono">
                {documentsOutline}
              </pre>
            </details>
            <div className="flex flex-wrap gap-4 text-sm text-gray-700">
              <label className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={createMissing}
                  onChange={(e) => setCreateMissing(e.target.checked)}
                />
                {t("hse.documents.json.create_missing")}
              </label>
              <label className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={atomic}
                  onChange={(e) => setAtomic(e.target.checked)}
                />
                {t("hse.documents.json.atomic")}
              </label>
            </div>
            <p className="text-xs text-amber-800">{t("hse.documents.json.create_missing_hint")}</p>
            <textarea
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setParseError("");
              }}
              spellCheck={false}
              className="min-h-[min(360px,45vh)] w-full flex-1 rounded-lg border border-gray-300 font-mono text-xs leading-relaxed focus:border-sky-500 focus:ring-2 focus:ring-sky-500"
            />
            {parseError ? (
              <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-800">{parseError}</p>
            ) : null}
            {importResult ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-950">
                <p className="font-medium">{t("hse.documents.json.result_title")}</p>
                <p className="mt-1 text-xs text-emerald-900">
                  {t("hse.documents.json.result_hint")}
                </p>
                {createdDocs.length > 0 ? (
                  <ul className="mt-2 space-y-1 text-sm">
                    {createdDocs.map((doc) => (
                      <li key={doc.id}>
                        <Link
                          className="text-sky-800 underline hover:text-sky-950"
                          to={`/dashboard/hse-ks/documents/${doc.id}/edit`}
                          onClick={onClose}
                        >
                          {doc.title || `#${doc.id}`}
                        </Link>
                        {doc.document_number ? (
                          <span className="ml-2 text-xs text-emerald-800">
                            {doc.document_number}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-xs">{t("hse.documents.json.result_no_docs")}</p>
                )}
                {Array.isArray(importResult.errors) && importResult.errors.length ? (
                  <ul className="mt-2 list-disc pl-5 text-xs text-red-800">
                    {importResult.errors.slice(0, 8).map((err) => (
                      <li key={err}>{err}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCopyJson}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ClipboardDocumentIcon className="h-5 w-5" />
                {t("hse.documents.json.copy_json")}
              </button>
              <button
                type="button"
                onClick={handleReloadStructureSample}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-5 w-5" />
                {t("hse.documents.json.reload_blank")}
              </button>
              <button
                type="button"
                onClick={handleInsertSample}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("hse.documents.json.insert_sample")}
              </button>
              <AIGenerator
                module="hse_document"
                config={aiGeneratorConfigs.hse_document_bulk}
                context={{
                  title: "",
                  description: "",
                  document_type: defaultDocumentType,
                  existing_structure: existingStructureJson,
                  existing_documents: existingDocumentsJson,
                }}
                fieldsToGenerate={["documents_json"]}
                onGenerate={(generatedFields) => {
                  const raw = generatedFields?.documents_json;
                  if (!raw) {
                    toast.error(t("hse.documents.json.ai_parse_error"));
                    return;
                  }
                  try {
                    const parsed = parseImportedHseDocumentsJson(
                      typeof raw === "string" ? raw : JSON.stringify(raw)
                    );
                    if (parsed.droppedCount > 0) {
                      toast.error(
                        t("hse.documents.json.dropped_rows", {
                          count: parsed.droppedCount,
                          detail: (parsed.dropReasons || []).slice(0, 5).join(", "),
                        })
                      );
                    }
                    setDraft(serializeHseDocumentsPayloadForExport(parsed));
                    setCreateMissing(Boolean(parsed.create_missing_structure));
                    setAtomic(Boolean(parsed.atomic));
                    setParseError("");
                    toast.success(
                      t("hse.documents.json.ai_apply_success", {
                        count: parsed.documents.length,
                      })
                    );
                    setTab("json");
                  } catch {
                    toast.error(t("hse.documents.json.ai_parse_error"));
                  }
                }}
                buttonLabel={t("hse.documents.json.generate_with_ai")}
              />
              <button
                type="button"
                onClick={handleApply}
                disabled={importing}
                className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
              >
                {importing
                  ? t("hse.documents.json.importing")
                  : t("hse.documents.json.apply")}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              {t("hse.documents.json.ai_guide_intro")}
            </p>
            <pre className="max-h-[min(420px,55vh)] overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-4 text-left font-mono text-xs leading-relaxed text-slate-800">
              {aiGuide}
            </pre>
            <button
              type="button"
              onClick={handleCopyAiGuide}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <ClipboardDocumentIcon className="h-5 w-5" />
              {t("hse.documents.json.copy_ai_guide")}
            </button>
          </>
        )}
      </div>
    </PMDialog>
  );
}
